import { copyToClipboard, useAuth } from "@elit-hair/elit-tools";
import {
    Badge,
    Button,
    DatePickerMode,
    EmptyState,
    Icon,
    PaginationControls,
    Spinner,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useIsMobile,
} from "@elit-hair/elit-ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { Fragment, useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { TbCopy, TbPhoneIncoming, TbPhoneOutgoing, TbPhoneX } from "react-icons/tb";
import { toast } from "sonner";

import { CallButton, CallButtonVariant } from "@/components/ui/call-button";
import { CALL_TYPES } from "@/constants/call";
import { PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import { CallsFilters, CallsViewMode } from "@/pages/calls/components/calls-filters";
import { useCallManagement } from "@/pages/calls/hooks/use-call-management";
import { CallsSearchParamsSchema } from "@/pages/calls/types/calls-search.types";
import {
    determineCallType,
    getBadgeVariantFromCallStatus,
    getCallQualification,
    useParseCalls,
} from "@/pages/calls/utils/calls";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { useCall } from "@/providers/hooks/use-call";
import { CallAnswerStatusFilter } from "@/types/call-answer-status-filter.types";
import { normalizeDurationSecondsBounds } from "@/types/call-duration-filter.types";
import { resolveCallMetricOverallScoreBounds } from "@/types/call-metric-filter.types";
import { normalizeOverallScoreBounds } from "@/types/call-overall-score-filter.types";
import { Call } from "@/types/call.types";
import { createPaginationProps } from "@/types/pagination.types";
import { convertFromDateRange, convertToDateRange } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

export const Route = createFileRoute(APP_ROUTES_FULL.CALLS)({
    component: Calls,
    validateSearch: searchValidator(CallsSearchParamsSchema),
});

function Calls() {
    const { startCall } = useCall();
    const search = Route.useSearch({
        structuralSharing: true,
    });
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const router = useRouter();
    const isMobile = useIsMobile();

    const { userData } = useAuth();
    const { permissionCodes } = useDashboardRole();
    const canSelectTeamMember = permissionCodes.includes(PERMISSIONS.CALL_CALLS_SELECT_MEMBER);
    const canUseGlobalCallsSearch = permissionCodes.includes(PERMISSIONS.CALL_CALLS_GLOBAL_SEARCH);

    const selectedUser = search.agent;
    const urlPage = search.page ?? PAGINATION.DEFAULT_PAGE;
    const [viewMode, setViewMode] = useState<CallsViewMode>(CallsViewMode.Member);

    const effectiveViewMode = canUseGlobalCallsSearch ? viewMode : CallsViewMode.Member;
    const callsUserId =
        effectiveViewMode === CallsViewMode.Global
            ? undefined
            : canSelectTeamMember
              ? (selectedUser ?? userData?.id)
              : userData?.id;

    const now = DateTime.now();
    const defaultStartDate = now.startOf("year").toISODate() ?? undefined;
    const defaultEndDate = now.toISODate() ?? undefined;
    const startDate = search.start ?? defaultStartDate;
    const endDate = search.end ?? defaultEndDate;

    const metricOverallScoreBounds = resolveCallMetricOverallScoreBounds(search.metric);
    const overallScoreMin = search.overallScoreMin ?? metricOverallScoreBounds?.overallScoreMin;
    const overallScoreMax = search.overallScoreMax ?? metricOverallScoreBounds?.overallScoreMax;

    const { data, isLoading, isError, error, refetch, handlers, params } = useCallManagement({
        userId: callsUserId,
        page: urlPage,
        startDate,
        endDate,
        answerStatus: search.answerStatus,
        durationSecondsMin: search.durationSecondsMin,
        durationSecondsMax: search.durationSecondsMax,
        overallScoreMin,
        overallScoreMax,
    });

    // Default date range to this year when opening the page without dates
    useEffect(() => {
        if (search.start && search.end) return;
        if (!defaultStartDate || !defaultEndDate) return;

        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                start: old.start ?? defaultStartDate,
                end: old.end ?? defaultEndDate,
            }),
            replace: true,
        });
    }, [search.start, search.end, defaultStartDate, defaultEndDate, navigate]);

    // Default member view to the current user ("my calls") when opening the page
    useEffect(() => {
        if (effectiveViewMode !== CallsViewMode.Member) return;
        if (!userData?.id) return;

        if (!canSelectTeamMember) {
            if (selectedUser === userData.id) return;
            navigate({
                to: Route.to,
                search: (old) => ({ ...old, agent: userData.id, page: undefined }),
                replace: true,
            });
            return;
        }

        if (selectedUser) return;
        navigate({
            to: Route.to,
            search: (old) => ({ ...old, agent: userData.id, page: undefined }),
            replace: true,
        });
    }, [canSelectTeamMember, effectiveViewMode, selectedUser, userData?.id, navigate]);

    const clearPageFromUrl = useCallback(() => {
        if (search.page === undefined) return;
        navigate({
            to: Route.to,
            search: (old) => ({ ...old, page: undefined }),
            replace: true,
        });
    }, [navigate, search.page]);

    const handlePageChange = useCallback(
        (page: number) => {
            handlers.setPage(page);
            navigate({
                to: Route.to,
                search: (old) => ({
                    ...old,
                    page: page === PAGINATION.DEFAULT_PAGE ? undefined : page,
                }),
                replace: true,
            });
        },
        [handlers, navigate]
    );

    // Parse calls data
    const { groupedCalls } = useParseCalls(data.data);

    // Create pagination props
    const paginationProps = createPaginationProps({
        response: data,
        onPageChange: handlePageChange,
        isLoading,
    });

    const handleMemberChange = (memberId: string | null) => {
        if (!canSelectTeamMember) return;

        const newUserId = memberId || undefined;
        navigate({
            to: Route.to,
            search: (old) => ({ ...old, agent: newUserId, page: undefined }),
            replace: true,
        });
        handlers.setUserId(newUserId);
    };

    const handleViewModeChange = (mode: CallsViewMode) => {
        if (!canUseGlobalCallsSearch || mode === viewMode) return;

        setViewMode(mode);

        if (mode === CallsViewMode.Global) {
            navigate({
                to: Route.to,
                search: (old) => ({ ...old, page: undefined }),
                replace: true,
            });
            handlers.setUserId(undefined);
            return;
        }

        const fallbackUserId = selectedUser ?? userData?.id;
        navigate({
            to: Route.to,
            search: (old) => ({ ...old, agent: fallbackUserId, page: undefined }),
            replace: true,
        });
        handlers.setUserId(fallbackUserId);
    };

    const handleCallButton = (call: Call) => {
        if (!call) return;

        const phoneNumber = call.calledNumber;

        startCall(phoneNumber, {
            userId: call.userId,
            orgId: call.orgId,
            moduleType: call.moduleType,
            recordId: call.recordId,
        });
    };

    const handleCopyCallId = async (callId: string) => {
        try {
            await copyToClipboard(callId);
            toast.success(t("call_id_copied_to_clipboard"));
        } catch (error) {
            console.error("Failed to copy call ID to clipboard:", error);
            toast.error(t("failed_to_copy_call_id"));
        }
    };

    const handleCopyCallDetailsUrl = useCallback(
        (callId: string, event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();

            const location = router.buildLocation({
                to: APP_ROUTES_TO.CALL_DETAILS,
                params: { callId },
            });
            const callDetailsUrl = new URL(location.href, window.location.origin);

            copyToClipboard(callDetailsUrl.toString());
            toast.success(t("link_copied_to_clipboard"));
        },
        [router, t]
    );

    // Convert date strings to DateRange for the date picker
    const dateRange = convertToDateRange(params.startDate, params.endDate);

    // Determine if it's single date mode (same start and end date)
    const isSingleDate = params.startDate && params.endDate && params.startDate === params.endDate;
    const [dateMode, setDateMode] = useState<DatePickerMode>(
        isSingleDate ? DatePickerMode.SINGLE : DatePickerMode.RANGE
    );

    const handleDateRangeChange = (range: DateRange | undefined) => {
        const { startDate, endDate } = convertFromDateRange(range);
        handlers.setDateRange(startDate, endDate);
        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                start: startDate,
                end: endDate,
                page: undefined,
            }),
            replace: true,
        });
    };

    const handleSearchChange = useCallback(
        (value: string) => {
            const nextSearch = value.length ? value : undefined;
            if (params.search === nextSearch) return;

            handlers.setSearch(value);
            clearPageFromUrl();
        },
        [handlers, clearPageFromUrl, params.search]
    );

    const handleAnswerStatusChange = (answerStatus?: CallAnswerStatusFilter) => {
        handlers.setAnswerStatus(answerStatus);
        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                answerStatus,
                page: undefined,
            }),
            replace: true,
        });
    };

    const handleDurationSecondsBoundsChange = (durationSecondsMin?: number, durationSecondsMax?: number) => {
        const normalized = normalizeDurationSecondsBounds(durationSecondsMin, durationSecondsMax);
        handlers.setDurationSecondsBounds(normalized.durationSecondsMin, normalized.durationSecondsMax);
        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                durationSecondsMin: normalized.durationSecondsMin,
                durationSecondsMax: normalized.durationSecondsMax,
                page: undefined,
            }),
            replace: true,
        });
    };

    const handleOverallScoreBoundsChange = (nextOverallScoreMin?: number, nextOverallScoreMax?: number) => {
        const normalized = normalizeOverallScoreBounds(nextOverallScoreMin, nextOverallScoreMax);
        handlers.setOverallScoreBounds(normalized.overallScoreMin, normalized.overallScoreMax);
        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                // Manual score edits replace the agent-performance metric preset
                metric: undefined,
                overallScoreMin: normalized.overallScoreMin,
                overallScoreMax: normalized.overallScoreMax,
                page: undefined,
            }),
            replace: true,
        });
    };

    const handleClearFilters = () => {
        handlers.clearAnswerStatusFilter();
        handlers.clearDurationFilter();
        handlers.clearOverallScoreFilter();
        navigate({
            to: Route.to,
            search: (old) => ({
                ...old,
                page: undefined,
                metric: undefined,
                answerStatus: undefined,
                durationSecondsMin: undefined,
                durationSecondsMax: undefined,
                overallScoreMin: undefined,
                overallScoreMax: undefined,
            }),
            replace: true,
        });
    };

    const today = DateTime.now().toJSDate();

    const filters = (
        <CallsFilters
            canFilterByMember={canSelectTeamMember}
            canUseGlobalCallsSearch={canUseGlobalCallsSearch}
            viewMode={effectiveViewMode}
            onViewModeChange={handleViewModeChange}
            selectedUser={selectedUser ?? userData?.id}
            onMemberChange={handleMemberChange}
            currentUserId={userData?.id}
            search={params.search || ""}
            onSearchChange={handleSearchChange}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            dateMode={dateMode}
            onDateModeChange={setDateMode}
            answerStatus={params.answerStatus}
            onAnswerStatusChange={handleAnswerStatusChange}
            durationSecondsMin={params.durationSecondsMin}
            durationSecondsMax={params.durationSecondsMax}
            onDurationSecondsBoundsChange={handleDurationSecondsBoundsChange}
            overallScoreMin={params.overallScoreMin}
            overallScoreMax={params.overallScoreMax}
            onOverallScoreBoundsChange={handleOverallScoreBoundsChange}
            maxDate={today}
            isMobile={isMobile}
            onClearFilters={handleClearFilters}
        />
    );

    return (
        <div className="flex flex-col h-full w-full max-w-full gap-4 min-w-0">
            {filters}
            <div className="flex-1 min-h-0 overflow-hidden rounded-lg border">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <Spinner size="lg" />
                    </div>
                ) : isError ? (
                    <div className="flex justify-center items-center h-full gap-4">
                        <p className="text-destructive">{(error as Error)?.message || "An error occurred"}</p>
                        <Button variant="outline" onClick={() => refetch()}>
                            {t("try_again")}
                        </Button>
                    </div>
                ) : groupedCalls.length === 0 ? (
                    <EmptyState variant="simple" title="no_calls_history_found" />
                ) : (
                    <div className="h-full flex flex-col bg-background overflow-hidden">
                        {/* Desktop Table View */}
                        <div className="hidden lg:flex flex-col h-full min-h-0">
                            <div className="flex-1 overflow-auto relative">
                                <table className="w-full max-w-full caption-bottom text-sm border-collapse">
                                    <thead className="sticky top-0 z-sticky bg-background">
                                        <tr className="border-b border-border">
                                            <th className="h-10 px-2 w-12 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                #
                                            </th>
                                            <th className="h-10 px-2 w-20 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("type")}
                                            </th>
                                            <th className="h-10 px-2 text-left align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("caller_name")}
                                            </th>
                                            <th className="h-10 px-2 text-left align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("caller_number")}
                                            </th>
                                            <th className="h-10 px-2 text-left align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("called_name")}
                                            </th>
                                            <th className="h-10 px-2 min-w-[140px] text-left align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("called_number")}
                                            </th>
                                            <th className="h-10 px-2 w-24 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("duration")}
                                            </th>
                                            <th className="h-10 px-2 w-32 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("status")}
                                            </th>
                                            <th className="h-10 px-2 w-32 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("analysis_result")}
                                            </th>
                                            <th className="h-10 px-2 w-40 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("time")}
                                            </th>
                                            <th className="h-10 px-2 w-40 text-center align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-background border-b border-border shadow-sm">
                                                {t("actions")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedCalls.map((dayGroup) => (
                                            <Fragment key={`day-${dayGroup.dayKey}`}>
                                                {/* Day Header Row */}
                                                <tr className="transition-colors">
                                                    <td
                                                        colSpan={11}
                                                        className="sticky top-10 z-[9] py-2 px-4 align-middle bg-muted/95 backdrop-blur-sm border-b-2 border-border/50 shadow-sm"
                                                    >
                                                        <div className="flex items-center justify-center gap-3">
                                                            <span className="text-sm font-semibold text-foreground">
                                                                {dayGroup.dayLabel}
                                                            </span>
                                                            <Badge variant="secondary" className="text-xs font-medium">
                                                                {dayGroup.calls.length} {t("calls")}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Calls for this day */}
                                                {dayGroup.calls.map((call) => {
                                                    const callType = determineCallType(call);
                                                    const callQualification = getCallQualification(call);
                                                    const arrowIcon =
                                                        callType === CALL_TYPES.INCOMING ? (
                                                            <Icon
                                                                icon={TbPhoneIncoming}
                                                                size={20}
                                                                className={
                                                                    call.status === "completed"
                                                                        ? "text-green-600 dark:text-green-400"
                                                                        : "text-red-600 dark:text-red-400"
                                                                }
                                                            />
                                                        ) : callType === CALL_TYPES.OUTGOING ? (
                                                            <Icon
                                                                icon={TbPhoneOutgoing}
                                                                size={20}
                                                                className={
                                                                    call.status === "completed"
                                                                        ? "text-green-600 dark:text-green-400"
                                                                        : "text-red-600 dark:text-red-400"
                                                                }
                                                            />
                                                        ) : (
                                                            <Icon
                                                                icon={TbPhoneX}
                                                                size={20}
                                                                className={
                                                                    call.status === "completed"
                                                                        ? "text-green-600 dark:text-green-400"
                                                                        : "text-red-600 dark:text-red-400"
                                                                }
                                                            />
                                                        );

                                                    return (
                                                        <tr
                                                            key={call.id}
                                                            className="cursor-pointer transition-colors hover:bg-muted/60 border-b border-border/50"
                                                            onClick={() =>
                                                                navigate({
                                                                    to: APP_ROUTES_TO.CALL_DETAILS,
                                                                    params: { callId: call.id },
                                                                })
                                                            }
                                                        >
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleCopyCallId(call.id);
                                                                            }}
                                                                        >
                                                                            {call.number}
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p className="font-mono text-xs">{call.id}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </td>
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <div className="flex justify-center">
                                                                    <Badge
                                                                        variant={
                                                                            callType === CALL_TYPES.MISSED
                                                                                ? "secondary"
                                                                                : callType === CALL_TYPES.INCOMING
                                                                                  ? "default"
                                                                                  : "secondary"
                                                                        }
                                                                        className="w-10 h-10 rounded-lg flex items-center justify-center p-0 border border-border/50 shadow-sm"
                                                                    >
                                                                        {arrowIcon}
                                                                    </Badge>
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle py-4">
                                                                <div className="truncate font-medium text-sm">
                                                                    {call.callerName}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle py-4">
                                                                <div className="truncate text-sm text-muted-foreground font-mono">
                                                                    {call.callerNumber}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle py-4">
                                                                <div className="truncate font-medium text-sm">
                                                                    {call.calledName}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle py-4">
                                                                <div className="truncate text-sm text-muted-foreground font-mono">
                                                                    {call.calledNumber}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <span className="text-sm font-medium">
                                                                    {call.callDurationFormatted}
                                                                </span>
                                                            </td>
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <div className="flex justify-center">
                                                                    <Badge
                                                                        label={t(`call_status_${call.status}`)}
                                                                        variant={getBadgeVariantFromCallStatus(
                                                                            call.status
                                                                        )}
                                                                        className="text-xs font-medium"
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <div className="flex justify-center">
                                                                    <Badge
                                                                        label={
                                                                            callQualification === "none"
                                                                                ? t("none")
                                                                                : t(callQualification)
                                                                        }
                                                                        variant={
                                                                            callQualification === "netto"
                                                                                ? "success"
                                                                                : callQualification === "brutto"
                                                                                  ? "warning"
                                                                                  : "secondary"
                                                                        }
                                                                        className="text-xs font-medium"
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle text-center py-4">
                                                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                                                    {call.createdAtFormatted}
                                                                </span>
                                                            </td>
                                                            <td
                                                                className="p-2 align-middle text-center py-4"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <div className="flex items-center justify-center gap-2">
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="icon"
                                                                                className="h-8 w-8"
                                                                                onClick={(e) =>
                                                                                    handleCopyCallDetailsUrl(call.id, e)
                                                                                }
                                                                            >
                                                                                <Icon icon={TbCopy} />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="top">
                                                                            {t("copy_transcript_link")}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                    <CallButton
                                                                        variant={CallButtonVariant.SQUARE}
                                                                        onCall={(e) => {
                                                                            e.stopPropagation();
                                                                            handleCallButton(call);
                                                                        }}
                                                                        tooltip={t("call")}
                                                                        tooltipSide="top"
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {paginationProps && paginationProps.totalPages > 1 && (
                                <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-4 py-2">
                                    <p className="text-xs text-muted-foreground">
                                        {t("showing_results", {
                                            start: (paginationProps.page - 1) * paginationProps.pageSize + 1,
                                            end: Math.min(
                                                paginationProps.page * paginationProps.pageSize,
                                                paginationProps.total
                                            ),
                                            total: paginationProps.total,
                                        })}
                                    </p>
                                    <PaginationControls
                                        currentPage={paginationProps.page}
                                        totalPages={paginationProps.totalPages}
                                        onPageChange={paginationProps.onPageChange!}
                                        isLoading={paginationProps.isLoading}
                                        className="gap-2"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden flex flex-col h-full min-h-0">
                            <div className="flex-1 overflow-auto relative">
                                <div className="space-y-3 p-3 pb-2">
                                    {groupedCalls.map((dayGroup) => (
                                        <div key={`day-${dayGroup.dayKey}`} className="space-y-3">
                                            {/* Day Header */}
                                            <div className="sticky top-0 z-sticky border-y border-border bg-background/95 backdrop-blur-sm px-4 py-2 -mx-4">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-semibold text-foreground">
                                                        {dayGroup.dayLabel}
                                                    </span>
                                                    <Badge variant="secondary" className="text-xs">
                                                        {dayGroup.calls.length} {t("calls")}
                                                    </Badge>
                                                </div>
                                            </div>

                                            {/* Call Cards */}
                                            {dayGroup.calls.map((call) => {
                                                const callType = determineCallType(call);
                                                const callQualification = getCallQualification(call);
                                                const arrowIcon =
                                                    callType === CALL_TYPES.INCOMING ? (
                                                        <Icon
                                                            icon={TbPhoneIncoming}
                                                            size={24}
                                                            className={
                                                                call.status === "completed"
                                                                    ? "text-green-600 dark:text-green-400"
                                                                    : "text-red-600 dark:text-red-400"
                                                            }
                                                        />
                                                    ) : callType === CALL_TYPES.OUTGOING ? (
                                                        <Icon
                                                            icon={TbPhoneOutgoing}
                                                            size={24}
                                                            className={
                                                                call.status === "completed"
                                                                    ? "text-green-600 dark:text-green-400"
                                                                    : "text-red-600 dark:text-red-400"
                                                            }
                                                        />
                                                    ) : (
                                                        <Icon
                                                            icon={TbPhoneX}
                                                            size={24}
                                                            className={
                                                                call.status === "completed"
                                                                    ? "text-green-600 dark:text-green-400"
                                                                    : "text-red-600 dark:text-red-400"
                                                            }
                                                        />
                                                    );

                                                return (
                                                    <div
                                                        key={call.id}
                                                        className="cursor-pointer rounded-lg border border-border bg-card p-3 shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
                                                        onClick={() =>
                                                            navigate({
                                                                to: APP_ROUTES_TO.CALL_DETAILS,
                                                                params: { callId: call.id },
                                                            })
                                                        }
                                                    >
                                                        <div className="mb-2 flex items-start justify-between gap-3">
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                <Badge
                                                                    variant={
                                                                        callType === CALL_TYPES.MISSED
                                                                            ? "secondary"
                                                                            : callType === CALL_TYPES.INCOMING
                                                                              ? "default"
                                                                              : "secondary"
                                                                    }
                                                                    className="w-12 h-12 rounded-lg flex items-center justify-center p-0 border border-border/50 shadow-sm shrink-0"
                                                                >
                                                                    {arrowIcon}
                                                                </Badge>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className="text-xs font-medium text-muted-foreground">
                                                                            #{call.number}
                                                                        </span>
                                                                        <Badge
                                                                            label={t(`call_status_${call.status}`)}
                                                                            variant={getBadgeVariantFromCallStatus(
                                                                                call.status
                                                                            )}
                                                                            className="text-xs"
                                                                        />
                                                                    </div>
                                                                    <p className="text-sm font-semibold text-foreground truncate">
                                                                        {call.callerName}
                                                                    </p>
                                                                    <p className="text-xs text-muted-foreground font-mono truncate">
                                                                        {call.callerNumber}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className="flex items-center gap-2 shrink-0"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="icon"
                                                                            className="h-8 w-8"
                                                                            onClick={(e) =>
                                                                                handleCopyCallDetailsUrl(call.id, e)
                                                                            }
                                                                        >
                                                                            <Icon icon={TbCopy} />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        {t("copy_transcript_link")}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                                <CallButton
                                                                    variant={CallButtonVariant.SQUARE}
                                                                    onCall={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCallButton(call);
                                                                    }}
                                                                    tooltip={t("call")}
                                                                    tooltipSide="top"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-1 border-t border-border/50 pt-2">
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-muted-foreground">
                                                                    {t("called_name")}:
                                                                </span>
                                                                <span className="font-medium text-foreground">
                                                                    {call.calledName}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-muted-foreground">
                                                                    {t("called_number")}:
                                                                </span>
                                                                <span className="font-mono text-foreground">
                                                                    {call.calledNumber}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-muted-foreground">
                                                                    {t("duration")}:
                                                                </span>
                                                                <span className="font-medium text-foreground">
                                                                    {call.callDurationFormatted}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-muted-foreground">
                                                                    {t("time")}:
                                                                </span>
                                                                <span className="text-foreground">
                                                                    {call.createdAtFormatted}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-muted-foreground">
                                                                    {t("analysis_result")}:
                                                                </span>
                                                                <Badge
                                                                    label={
                                                                        callQualification === "none"
                                                                            ? t("none")
                                                                            : t(callQualification)
                                                                    }
                                                                    variant={
                                                                        callQualification === "netto"
                                                                            ? "success"
                                                                            : callQualification === "brutto"
                                                                              ? "warning"
                                                                              : "secondary"
                                                                    }
                                                                    className="text-xs"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pagination Controls */}
                            {paginationProps && paginationProps.totalPages > 1 && (
                                <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2">
                                    <p className="min-w-0 text-[11px] leading-tight text-muted-foreground">
                                        {t("showing_results", {
                                            start: (paginationProps.page - 1) * paginationProps.pageSize + 1,
                                            end: Math.min(
                                                paginationProps.page * paginationProps.pageSize,
                                                paginationProps.total
                                            ),
                                            total: paginationProps.total,
                                        })}
                                    </p>
                                    <PaginationControls
                                        currentPage={paginationProps.page}
                                        totalPages={paginationProps.totalPages}
                                        onPageChange={paginationProps.onPageChange!}
                                        isLoading={paginationProps.isLoading}
                                        showPageInfo={false}
                                        className="shrink-0 gap-1"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
